import { useEffect, useRef, useState } from "react";
import { SurveyNav } from "../components/survey/SurveyNav";
import { ThinkingDots } from "../components/survey/ThinkingDots";
import { InitiativeLeaderboard } from "../components/survey/InitiativeLeaderboard";
import { TypewriterText } from "../components/interview/TypewriterText";
import {
  fetchAggregate,
  askRoom,
  type InitiativeTally,
  type SurveyStats,
} from "../lib/survey-aggregate-api";

const STARTERS = [
  "What is working best across the business?",
  "What is quietly broken?",
  "Which initiatives landed best, and which fell flat?",
  "What should we change for the back half of 2026?",
];

type AnswerState =
  | { kind: "loading" }
  | { kind: "answer"; text: string; lightInput?: boolean }
  | { kind: "budget" }
  | { kind: "error" };

type Turn = { q: string; a: AnswerState };

export default function AskTheRoom() {
  const [stats, setStats] = useState<SurveyStats | null>(null);
  const [tallies, setTallies] = useState<InitiativeTally[]>([]);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const threadEndRef = useRef<HTMLDivElement>(null);

  const busy = turns.some((t) => t.a.kind === "loading");
  const started = turns.length > 0;

  useEffect(() => {
    void fetchAggregate().then((r) => {
      if (r.ok) {
        setStats(r.stats ?? null);
        setTallies(r.tallies ?? []);
      }
    });
  }, []);

  const ask = async (q: string) => {
    const query = q.trim();
    if (!query || busy) return;
    setDraft("");
    const index = turns.length;
    setTurns((prev) => [...prev, { q: query, a: { kind: "loading" } }]);
    window.setTimeout(
      () => threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }),
      50,
    );

    const r = await askRoom(query, threadId);

    const setAnswer = (a: AnswerState) =>
      setTurns((prev) => prev.map((t, i) => (i === index ? { ...t, a } : t)));

    if (r.ok && "answer" in r) {
      setStats(r.stats);
      setTallies(r.tallies);
      setThreadId(r.threadId);
      setAnswer({ kind: "answer", text: r.answer, lightInput: r.lightInput });
    } else if (r.ok && "budgetExceeded" in r) {
      setStats(r.stats);
      setTallies(r.tallies);
      setAnswer({ kind: "budget" });
    } else {
      setAnswer({ kind: "error" });
    }
    window.setTimeout(
      () => threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }),
      50,
    );
  };

  return (
    <main className="min-h-screen px-6 py-8 sm:py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-10">
          <SurveyNav current="ask" />
        </div>

        <div className="text-center space-y-3 mb-10">
          <h1 className="font-display text-4xl sm:text-5xl text-ink-900 leading-[1.1]">Our Voices</h1>
          <p className="text-ink-500 leading-relaxed max-w-xl mx-auto">
            Ask anything about what the leadership team thinks, then keep the conversation going.
            Answers come straight from everyone's input, and your follow-ups join it, anonymously.
            {stats && (
              <span className="block mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-300">
                {stats.completed.toLocaleString()} {stats.completed === 1 ? "voice" : "voices"} so far
              </span>
            )}
          </p>
          <a
            href="/survey?more=1"
            className="inline-block text-xs uppercase tracking-[0.16em] text-accent-600 underline-offset-4 hover:underline"
          >
            Have more to add? Contribute another thought
          </a>
        </div>

        {/* Thread */}
        {started && (
          <div className="space-y-4 mb-8">
            {turns.map((t, i) => (
              <div key={i} className="space-y-3">
                {/* User turn */}
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-ink-900 text-parchment-50 px-4 py-2.5 text-[15px] leading-relaxed">
                    {t.q}
                  </div>
                </div>
                {/* Room turn */}
                <div
                  className="rounded-2xl border border-parchment-300 bg-parchment-100 p-5 sm:p-6 soft-in"
                  aria-live={i === turns.length - 1 ? "polite" : undefined}
                >
                  {t.a.kind === "loading" && (
                    <div className="py-1">
                      <ThinkingDots label="Reading the room" />
                    </div>
                  )}
                  {t.a.kind === "answer" && (
                    <div className="font-sans text-ink-900 leading-[1.7] whitespace-pre-wrap">
                      <TypewriterText text={t.a.text} isLive={false} />
                      {t.a.lightInput && (
                        <div className="mt-5 pt-4 border-t border-parchment-300/70 flex items-center justify-between gap-3 flex-wrap">
                          <p className="text-xs text-ink-500 leading-snug">
                            Input is still light, so this is an early read.
                          </p>
                          <a
                            href="/survey"
                            className="text-xs uppercase tracking-[0.14em] text-accent-600 hover:underline underline-offset-4 whitespace-nowrap"
                          >
                            Add your voice &rarr;
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  {t.a.kind === "budget" && (
                    <p className="text-ink-700 leading-relaxed">
                      The written answers are paused for today (we reached the daily limit). The live
                      numbers below are always current, and answers come back tomorrow.
                    </p>
                  )}
                  {t.a.kind === "error" && (
                    <p className="text-ink-700">Could not read the room just now. Try again in a moment.</p>
                  )}
                </div>
              </div>
            ))}
            <div ref={threadEndRef} />
          </div>
        )}

        {/* Question box */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void ask(draft);
          }}
          className="space-y-3"
        >
          <div className="flex items-center gap-2 rounded-2xl border border-parchment-300 bg-parchment-100 px-4 py-2 focus-within:border-ink-300 transition-colors">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={started ? "Add a thought or ask a follow-up…" : "Ask the leadership team a question…"}
              className="flex-1 bg-transparent py-2 text-ink-900 placeholder:text-ink-300/80 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!draft.trim() || busy}
              className="px-5 py-2 rounded-full text-sm font-medium bg-ink-900 text-parchment-50 hover:bg-ink-700 disabled:bg-parchment-200 disabled:text-ink-300 transition-all"
            >
              {busy ? "Reading…" : started ? "Send" : "Ask"}
            </button>
          </div>
          {!started && (
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => void ask(s)}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-full text-xs text-ink-500 border border-parchment-300 bg-parchment-50 hover:border-ink-300 transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </form>

        {/* Live leaderboard */}
        <section className="mt-14">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl text-ink-900">How the initiatives are landing</h2>
            <span className="text-[11px] uppercase tracking-[0.14em] text-ink-300">live</span>
          </div>
          <InitiativeLeaderboard tallies={tallies} />
          <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-ink-500">
            <Legend color="#C8A24A" label="Gold" />
            <Legend color="#B8B5AD" label="Silver" />
            <Legend color="#B07A4A" label="Bronze" />
            <Legend color="#B25B4C" label="Didn't land" />
          </div>
        </section>
      </div>
    </main>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}
